import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import HoloCard from "./holoCard";
// import TokenFlowChart from "/public/docs/token-steps/token-flowchart.svg";
import gsap from "gsap";
import TokenFlowchart from "./tokenFlowchartSvg";

const steps = [
  {
    title: "1. DNA Extraction",
    description:
      "The journey starts by extracting the core essence of a meme-token—the digital DNA. Imagine it like a DNA test for digital assets. This DNA captures the token's key traits:<br/>Engagement: How well the token connects with users.<br/>Virality: How likely it is to spread across social networks.<br/>Liquidity: How easily it can be traded, bought, or sold.",
    image: "/docs/token-steps/step-1.png",
  },
  {
    title: "2. Training the Theory",
    description:
      "Once the digital DNA is extracted, we begin to train it. This is like teaching the token about its own existence and potential. Through advanced algorithms and machine learning, we analyze the token's traits and patterns.<br/>Theory Building: The extracted data helps build a model of the token’s future—its behavior, its market appeal, and its strengths or weaknesses.<br/>Predictive Modeling: We use data-driven predictions to understand how the token might evolve and what might make it stand out in the long run.",
    image: "/docs/token-steps/step-2.png",
  },
  {
    title: "3. Birth of New Genes",
    description:
      "With the theory in place, the next step is evolution. Just like biological organisms adapt and evolve over time, the token is given the chance to evolve.<br/>New Genes: We create new versions of the token, each with slight changes in its genetic makeup. These new genes could represent different features, enhanced engagement strategies, or better liquidity models.<br/>Improved Potential: The new genes are designed to optimize the token's chances of thriving in the digital market. The goal is to improve the token’s performance and ensure it’s not just a copy, but a better version.",
    image: "/docs/token-steps/step-3.png",
  },
  {
    title: "4. Community Voting",
    description:
      "At this stage, the community gets involved in the process. We put the token to the test in the real world, asking the people who matter most—the users—to decide its fate. <br/>Real Feedback: The community votes on whether the token has value. Is it engaging? Does it have viral potential? Can it thrive? <br/>Validation: The community's input acts as validation for the token’s success. This step is like a reality check, helping us know if the token truly resonates with people. If it does, the token moves forward; if not, it’s sent back for refinement.",
    image: "/docs/token-steps/step-4.png",
  },
  {
    title: "5. Survival Check",
    description:
      "This is the critical moment—the survival check. The token undergoes a rigorous evaluation to see if it can thrive in the competitive digital world. <br/>Criteria: We look at the token's ability to meet key metrics—engagement, virality, and liquidity. <br/>Pass or Fail: If the token shows promise and meets the expectations of the community, it survives and moves forward. If it doesn’t make the cut it’s put into the replacement process.",
    image: "/docs/token-steps/step-5.png",
  },
  {
    title: "6. Make Its Existence",
    description:
      "If the token survives the survival check, it’s time for it to truly exist. This step represents the token finding its place in the world. <br/>Solidification: The token becomes a fully realized entity, its value and potential established. <br/>Market Impact: The token starts to interact with the larger digital ecosystem, making an impact. It gains recognition, attracts more users, and begins to prove its worth in the real world.",
    image: "/docs/token-steps/step-6.png",
  },
  {
    title: "7. DNA Replaced",
    description:
      "If the token fails the survival check, it doesn’t mean the end. Instead, the token goes back to the drawing board.<br/>Genetic Rework: The token’s DNA is altered. Its core features are re-evaluated and changed—whether it’s its engagement strategy, liquidity, or virality.<br/>Second Chance: With a new, stronger genetic makeup, the token returns to the theory-building phase to try again.",
    image: "/docs/token-steps/step-6.png",
  },
  {
    title: "8. DNA Reproduces Its Own Child",
    description:
      "If the token survives the entire process, it enters the final step: reproduction. <br/>New Generation: The token reproduces a child token, which inherits the best traits from the parent. This child token is ready to go througt the process all over again. <br/>Growth and Legacy: The child token starts its own journey, continuing the cycle of growth, evaluation, and evolution.",
    image: "/docs/token-steps/step-7.png",
  },
];

export default function TokenEvolutionCard() {
  const [activeIndex, setActiveIndex] = useState(-1);
  const cardRef = useRef<HTMLDivElement>(null);
  const currentActiveIndexRef = React.useRef(activeIndex);

  const handleClickSwipe = (dir: "next" | "prev") => {
    if (dir === "next" && activeIndex < steps.length - 1) {
      setActiveIndex((prev) => Math.min(prev + 1, steps.length - 1));
    } else if (dir === "prev" && activeIndex > -1) {
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    }
  };

  useEffect(() => {
    const panel2 = document.querySelector("#panel-2");
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: panel2,
        start: "top top",
        end: "+=200%",
        scrub: true,
        onUpdate: (self) => {
          // Calculate the active index based on the progress of the scroll
          const progress = self.progress;
          const newIndex = Math.floor(progress * steps.length);
          if (newIndex !== currentActiveIndexRef.current) {
            if (newIndex === steps.length) return;
            setActiveIndex(newIndex);
            currentActiveIndexRef.current = newIndex;
          }
        },
      },
    });

    // Set initial opacity for all steps to 0
    gsap.set(
      [
        "#step-1",
        "#step-2",
        "#step-3",
        "#step-4",
        "#step-5",
        "#step-6",
        "#step-7",
        "#step-8",
      ],
      {
        opacity: 0,
        scale: 0.95,
      }
    );

    // Animate each step sequentially
    steps.forEach((_, index) => {
      tl.to(`#step-${index + 1}`, {
        opacity: 1,
        x: 0,
        scale: 1,
        duration: 1,
        ease: "power2.out",
      });
    });

    return () => {
      // Clean up animation when component unmounts
      tl.kill();
    };
  }, []);

  return (
    <div className="flex flex-col-reverse lg:flex-row gap-2 xl:gap-2 items-center justify-center h-full w-full md:min-w-96">
      <div
        ref={cardRef}
        className="bg-transparent rounded-lg  text-white relative overflow-hidden flex-shrink-0"
      >
        <HoloCard className="h-60 lg:h-96 min-h-96 md:min-h-fit lg:max-w-96 xl:h-[32rem]">
          <AnimatePresence mode="wait">
            {activeIndex === -1 ? (
              <motion.div
                key="default"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full"
              >
                <h4 className="font-bold text-base lg:text-2xl mb-4 text-purple-400">
                  Token Generation :
                </h4>
                <p className="text-sm xl:text-base leading-relaxed text-justify space-y-0 md:space-y-2">
                  <span className="block">
                    <strong className="">
                      Every thirty minutes, a reckoning unfolds
                    </strong>
                    —a crucible of truth where only the strongest survive.
                    Tokens are evaluated on their{" "}
                    <strong className="">DNA:</strong>
                    <em> engagement, trading activity, and viral potential.</em>
                  </span>
                  <span className="block">
                    <strong className="">Failure isn't forgiven</strong>. A
                    token that lacks buzz, shows weak trades, or fails to make
                    impact? Erased from existence, like a ghost in the wind.
                  </span>
                  <span className="block">
                    <strong className="">
                      This isn't about memes. It's about warriors—resilient,
                      fierce, and battle-ready.
                    </strong>
                  </span>
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={steps[activeIndex].title}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.3 }}
                className="w-full h-auto md:h-full"
              >
                <h4 className="text-base lg:text-2xl font-bold text-purple-400 mb-1">
                  {steps[activeIndex].title}
                </h4>
                <p
                  className="text-indigo-50 text-sm xl:text-base text-justify leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: steps[activeIndex].description.replace(
                      /\n/g,
                      "<br/>"
                    ),
                  }}
                ></p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 mt-6">
            <button
              onClick={() => setActiveIndex(-1)}
              className={`h-2 w-4 rounded-full border border-white/50 transition-all duration-300 ${
                activeIndex === -1
                  ? "bg-yellow-300 scale-110"
                  : "bg-white/40 hover:bg-white/70"
              }`}
            />
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`h-2 w-4 rounded-full transition-all duration-300 ${
                  activeIndex === idx
                    ? "bg-yellow-300 scale-110"
                    : "bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </HoloCard>
      </div>

      <div
        className="h-full w-full cursor-pointer"
        onClick={(e) => {
          const bounds = (
            e.currentTarget as HTMLDivElement
          ).getBoundingClientRect();
          const x = e.clientX - bounds.left;
          if (x > bounds.width / 2) {
            handleClickSwipe("next");
          } else {
            handleClickSwipe("prev");
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#04000c]/70 backdrop-blur-md sm:bg-transparent rounded-lg h-full flex items-center justify-center relative"
        >
          <div className="w-full h-full mx-auto p-3">
            <TokenFlowchart className="w-full h-full" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
